import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  CreditCard,
  Crown,
  Mail,
  Shield,
  Trash2,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { UserAvatar } from "../../common/components/UserAvatar";
import { SPACING } from "../../common/constants/spacing";
import { useAuth } from "../../common/contexts/AuthContext";

function Overview() {
  const { t } = useTranslation("account");
  const navigate = useNavigate();
  const { subscriptionPlan, user } = useAuth();

  const accountMenuItems = [
    {
      title: t("overview.menuItems.security.title"),
      description: t("overview.menuItems.security.description"),
      icon: <Shield size={20} color="currentColor" />,
      path: "/account/security",
    },
    {
      title: t("overview.menuItems.membership.title"),
      description: t("overview.menuItems.membership.description"),
      icon: <CreditCard size={20} color="currentColor" />,
      path: "/account/membership",
    },
    {
      title: t("overview.menuItems.deleteAccount.title"),
      description: t("overview.menuItems.deleteAccount.description"),
      icon: <Trash2 size={20} color="currentColor" />,
      path: "/account/delete",
      danger: true,
    },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: SPACING.sm,
                }}
              >
                <UserAvatar name={user?.name} image={user?.avatar} size={80} />
              </Box>
              <Typography variant="h3" sx={{ mb: SPACING.xs, fontWeight: 500 }}>
                {user?.name || "User"}
              </Typography>
              <Typography
                variant="bodySm"
                color="text.secondary"
                sx={{ mb: SPACING.sm }}
              >
                {user?.email || "user@example.com"}
              </Typography>
              <Box sx={{ mt: SPACING.sm }}>
                <Chip
                  icon={<Mail size={16} style={{ marginRight: 0.5 }} />}
                  label={
                    user?.is_email_verified
                      ? t("overview.verified")
                      : t("overview.notVerified")
                  }
                  color={user?.is_email_verified ? "primary" : "error"}
                  size="small"
                  sx={{ mb: SPACING.xs, px: 1, py: 2 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                {t("overview.accountInformation")}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      py: SPACING.sm,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: SPACING.xs,
                      }}
                    >
                      <User
                        size={16}
                        color="currentColor"
                        style={{ marginRight: 8, color: "text.secondary" }}
                      />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("overview.fullName")}
                      </Typography>
                    </Box>
                    <Typography variant="bodyMd" sx={{ fontWeight: 500 }}>
                      {user?.name || t("overview.notProvided")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      py: SPACING.sm,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: SPACING.xs,
                      }}
                    >
                      <Mail
                        size={16}
                        color="currentColor"
                        style={{ marginRight: 8, color: "text.secondary" }}
                      />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("overview.emailAddress")}
                      </Typography>
                    </Box>
                    <Typography variant="bodyMd" sx={{ fontWeight: 500 }}>
                      {user?.email || t("overview.notProvided")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      py: SPACING.sm,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: SPACING.xs,
                      }}
                    >
                      <Calendar
                        size={16}
                        color="currentColor"
                        style={{ marginRight: 8, color: "text.secondary" }}
                      />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("overview.memberSince")}
                      </Typography>
                    </Box>
                    <Typography variant="bodyMd" sx={{ fontWeight: 500 }}>
                      {formatDate(user?.date_joined)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      py: SPACING.sm,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: SPACING.xs,
                      }}
                    >
                      <CheckCircle
                        size={16}
                        color="currentColor"
                        style={{ marginRight: 8, color: "text.secondary" }}
                      />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("overview.accountStatus")}
                      </Typography>
                    </Box>
                    <Chip
                      label={t("overview.active")}
                      color="success"
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      py: SPACING.sm,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: SPACING.xs,
                      }}
                    >
                      <Crown
                        size={16}
                        color="currentColor"
                        style={{ marginRight: 8, color: "text.secondary" }}
                      />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("overview.subscriptionPlan")}
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        subscriptionPlan
                          ? subscriptionPlan.charAt(0).toUpperCase() +
                            subscriptionPlan.slice(1)
                          : t("overview.free")
                      }
                      color={
                        subscriptionPlan === "premium"
                          ? "primary"
                          : subscriptionPlan === "basic"
                            ? "secondary"
                            : "default"
                      }
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h3" sx={{ mb: 3 }}>
                {t("overview.accountSettings")}
              </Typography>
              <List sx={{ p: 0 }}>
                {accountMenuItems.map((item, index) => (
                  <Box key={item.title}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 2,
                        borderRadius: 1,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: item.danger ? "error.main" : "inherit",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: item.danger ? "error.main" : "inherit",
                            }}
                          >
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="bodySm" color="text.secondary">
                            {item.description}
                          </Typography>
                        }
                      />
                      <IconButton
                        color={item.danger ? "error" : "primary"}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(item.path);
                        }}
                      >
                        <ArrowRight />
                      </IconButton>
                    </ListItem>
                    {index < accountMenuItems.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Overview;
